import React from 'react'
import {
  IconAITest,
  IconDefaultTest,
  IconQTIUploadTest,
  IconSmartBuildTest,
  IconSnapQuizTest,
  IconSurveyTest,
  IconVideoQuizTest,
  IconWhiteLightBulb,
  IconPremiumStar,
  IconStar,
  IconSectionAdaptiveTest,
  IconContentConverter,
} from '@edulastic/icons'
import { navigationState } from '../../../src/constants/navigation'
import { AddOnIconWrapper, AddOnTextWrapper } from './Components/styled'
import { TEST_IMPORT_KEYS } from '../TestImport/constants'

export const AccessType = {
  OR: 'OR',
  AND: 'AND',
}

export const TestKeys = {
  DEFAULT_TEST: 'default',
  DYNAMIC_TEST: 'dynamic',
  SECTION_ADAPTIVE_TEST: 'sectionAdaptiveTest',
  AI_TEST: 'ai',
  SNAP_QUIZ_TEST: 'snapquiz',
  VIDEO_QUIZ_TEST: 'videoquiz',
  GOOGLE_FORM_TEST: 'googleform',
  IMPORT_TEST: 'importtest',
  QTI_IMPORT_TEST: 'qtiimport',
  SURVEY_TEST: 'survey',
}

export const CREATE_TEST_SEGMENT_EVENT_NAME = {
  TEST_IMPORT_CLICK: 'TestImportClick',
}

export const AccessNavigation = {
  PREMIUM: {
    key: 'premium',
    icon: <IconPremiumStar />,
    infoMessage: 'Buy Premium to unlock this feature.',
    navigation: {
      pathname: '/author/subscription',
    },
  },
  PREMIUM_AI_ADDON: {
    key: 'isVideoQuizAndAIEnabled',
    icon: <IconPremiumStar />,
    infoMessage: (
      <>
        <AddOnIconWrapper>
          <IconStar />
          <AddOnTextWrapper>ADD-ON</AddOnTextWrapper>
        </AddOnIconWrapper>
        <div>Buy VideoQuiz and AI Suite to unlock this feature.</div>
      </>
    ),
    navigation: {
      pathname: '/author/subscription',
      state: { view: navigationState.SUBSCRIPTION.view.ADDON },
    },
  },
  DATA_WAREHOUSE_REPORTS: {
    key: 'dataWarehouseReports',
    icon: <IconWhiteLightBulb style={{ fill: 'transparent' }} />,
    infoMessage: 'Purchase Data Studio to use Surveys.',
    navigation: {
      pathname: '/author/subscription',
      state: { view: navigationState.SUBSCRIPTION.view.DATA_STUDIO },
    },
  },
  INSTANT_LESSON_PACKAGE: {
    key: 'allowInstantLessonPackage',
  },
}
export const TestSections = [
  {
    key: 'default-section',
    title: 'Build Your Own Test',
    tests: [
      {
        key: TestKeys.DEFAULT_TEST,
        icon: <IconDefaultTest />,
        heading: 'From Scratch',
        description: 'Choose from Item Bank or author with 50+ question types',
        navigation: {
          pathname: '/author/tests/create',
        },
      },
      {
        key: TestKeys.SECTION_ADAPTIVE_TEST,
        icon: <IconSectionAdaptiveTest />,
        heading: 'Multi-Section / Adaptive',
        description:
          'Section-based Tests with PSAT aligned adaptive capabilities',
        access: {
          type: AccessType.OR,
          features: [AccessNavigation.PREMIUM],
        },
        navigation: {
          pathname: '/author/tests/create',
          state: { hasSections: true },
        },
        isNew: true,
      },
      {
        key: TestKeys.DYNAMIC_TEST,
        icon: <IconSmartBuildTest />,
        heading: 'Smart Build',
        description: 'Instantly draw from an item bank or build your own pool',
        quickTour: {
          title: 'Get Started with SmartBuild',
          infoMessage: 'Watch Quick Tour',
          url: '//fast.wistia.net/embed/iframe/mcobek8kl5',
        },
        segmentEvent: {
          name: 'DynamicTestCreateTestClick',
        },
        navigation: {
          pathname: '/author/tests/create',
          state: { isDynamicTest: true },
        },
      },
      {
        key: TestKeys.AI_TEST,
        icon: <IconAITest />,
        heading: 'Using AI',
        description: 'Use AI to create tests quickly',
        access: {
          type: AccessType.OR,
          features: [
            AccessNavigation.PREMIUM_AI_ADDON,
            AccessNavigation.INSTANT_LESSON_PACKAGE,
          ],
        },
      },
    ],
  },
  {
    key: 'import-section',
    title: 'Use External Content',
    tests: [
      {
        key: TestKeys.SNAP_QUIZ_TEST,
        icon: <IconSnapQuizTest />,
        heading: 'Snap Quiz',
        description: 'Upload PDF to build an interactive PDF quiz',
        navigation: {
          pathname: '/author/tests/snapquiz',
        },
      },
      {
        key: TestKeys.VIDEO_QUIZ_TEST,
        icon: <IconVideoQuizTest />,
        heading: 'Video Quiz',
        description:
          'Convert a video from YouTube, Vimeo, Wistia to interactive test',
        quickTour: {
          title: 'Get Started with VideoQuiz',
          infoMessage: 'Watch Quick Tour',
          url: '//fast.wistia.net/embed/iframe/jd8y6sdt1m',
        },
        segmentEvent: {
          name: 'VideoQuizCreateTestClick',
          data: {
            source: 'Test Library',
          },
        },
        navigation: {
          pathname: '/author/vqlibrary',
        },
        access: {
          type: AccessType.OR,
          features: [AccessNavigation.PREMIUM],
        },
      },
      {
        key: TestKeys.IMPORT_TEST,
        icon: <IconContentConverter />,
        heading: 'Content Converter',
        description: 'Convert a document or Google Form into a test',
        isNew: true,
        navigation: {
          pathname: '/author/tests/import',
          state: { view: TEST_IMPORT_KEYS.GOOGLE_FORMS },
        },
        segmentEvent: {
          name: CREATE_TEST_SEGMENT_EVENT_NAME.TEST_IMPORT_CLICK,
          data: {
            source: 'Test Library - Google Form/Doc',
          },
        },
      },
      {
        key: TestKeys.QTI_IMPORT_TEST,
        icon: <IconQTIUploadTest />,
        heading: 'Import Items to Test',
        description: 'Import Items from WebCT or QTI package and create a test',
        navigation: {
          pathname: '/author/tests/import',
          state: { view: TEST_IMPORT_KEYS.QTI },
        },
        segmentEvent: {
          name: CREATE_TEST_SEGMENT_EVENT_NAME.TEST_IMPORT_CLICK,
          data: {
            source: 'Test Library - QTI/WebCT',
          },
        },
      },
    ],
  },
  {
    id: 'survey-section',
    title: 'Surveys',
    tests: [
      {
        key: TestKeys.SURVEY_TEST,
        icon: <IconSurveyTest />,
        heading: 'Surveys',
        description: 'Build quick polls / surveys',
        access: {
          type: AccessType.OR,
          features: [AccessNavigation.DATA_WAREHOUSE_REPORTS],
        },
        navigation: {
          pathname: '/author/tests/create',
          search: 'testType=survey',
        },
        isNew: true,
      },
    ],
  },
]
